import {
  createCountryService,
  deleteCountryService,
  getCountriesService,
  getCountryByIdService,
  updateCountryService,
} from "@/services/countries.service";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const COUNTRIES_KEY = ["countries"] as const;

interface CountryFilters {
  page?: number;
  limit?: number;
  isAvailable?: boolean;
}

export const useCountries = (filters: CountryFilters = {}) =>
  useQuery({
    queryKey: [...COUNTRIES_KEY, filters],
    queryFn: async () => {
      const {data} = await getCountriesService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useCountry = (id: string) =>
  useQuery({
    queryKey: [...COUNTRIES_KEY, id],
    queryFn: async () => {
      const {data} = await getCountryByIdService(id);
      if (data === undefined) throw new Error("No data");
      return data;
    },
    enabled: !!id,
  });

export const useCountriesStats = () => {
  const query = useQuery({
    queryKey: [...COUNTRIES_KEY, "stats"],
    queryFn: async () => {
      const {data} = await getCountriesService({limit: 100});
      if (data === undefined) throw new Error("No data");
      return data;
    },
    staleTime: 60_000,
  });
  return {
    total: query.data?.pagination.total,
    active: query.data?.items.filter((c: {isAvailable: boolean}) => c.isAvailable).length,
  };
};

export const useCreateCountry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {name: string; currency: string; currencyCode: string; phoneCode: string; rate: number}) =>
      createCountryService(body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COUNTRIES_KEY});
    },
  });
};

export const useUpdateCountry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<{
        name: string;
        currency: string;
        currencyCode: string;
        phoneCode: string;
        rate: number;
        isAvailable: boolean;
      }>;
    }) => updateCountryService(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COUNTRIES_KEY});
    },
  });
};

export const useDeleteCountry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCountryService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COUNTRIES_KEY});
    },
  });
};
